import {skipToken} from '@reduxjs/toolkit/query';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantV2Query} from '@dms/api/base';
import {type CustomerResponseBodyV2, useGetCustomerV2Query} from '@dms/api/customer';
import {useGetInterestV2Query, useGetMinipurchaseQuery} from '@dms/api/finance';
import {useGetRentalOrderQuery} from '@dms/api/rental';
import {useGetAiLeadEmailMessageQuery, useGetBusinessCaseQuery} from '@dms/api/sales';
import {useGetSaleVehicleQuery, useGetVehicleQuery} from '@dms/api/vehicle';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseByServiceOrderIdQuery,
  useGetServiceOrderQuery,
  useGetWorkshopInterestQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';

import type {ComposeMessageContext} from '@prompt-bridge/shared/validations';

import {EMPTY_PLACEHOLDER, type Nullish} from 'shared/web';

import {getCustomerName} from '../../utils/getCustomerName';
import type {AiComposerRecord} from './AiComposerDraft';
import {
  type AiComposerContextSource,
  projectAiComposerRecordContext,
} from './projectAiComposerRecordContext';

interface Input {
  record: AiComposerRecord | Nullish;
  isEnabled: boolean;
}

interface RecordContext {
  context: ComposeMessageContext | null;
  isLoading: boolean;
  isError: boolean;
}

interface QueryState {
  isError: boolean;
  isFetching: boolean;
  isUninitialized: boolean;
}

const customerName = (customer: CustomerResponseBodyV2) => {
  const name = getCustomerName(customer);
  return name === EMPTY_PLACEHOLDER ? undefined : name;
};

// eslint-disable-next-line complexity -- Pre-existing violation
export function useAiComposerRecordContext(params: Input): RecordContext {
  const record = params.record;
  const enabled =
    params.isEnabled &&
    isNotNil(record?.context) &&
    isNotNilOrEmpty(record?.recordId) &&
    isNotNilOrEmpty(record?.customerId);
  const id = enabled ? record?.recordId : null;
  const kind = enabled ? record?.context : null;
  const tenant = useGetTenantV2Query(undefined, {skip: !enabled});
  const recipient = useGetCustomerV2Query(
    enabled && isNotNil(record?.customerId) ? {customerId: record.customerId} : skipToken
  );
  const customer = useGetCustomerV2Query(
    kind === 'CUSTOMER' && isNotNil(id) ? {customerId: id} : skipToken
  );
  const directVehicle = useGetVehicleQuery(
    kind === 'VEHICLE' && isNotNil(id) ? {vehicleId: id} : skipToken
  );
  const interest = useGetInterestV2Query(kind === 'INTEREST' && isNotNil(id) ? {id} : skipToken);
  // The enquiry lives next to the interest, not on it: the lead e-mail that opened it and the
  // first purchase wish. Both are optional reads; a denied or missing one only hides the offer.
  const leadMessageId = kind === 'INTEREST' ? interest.currentData?.originLeadMessageId : null;
  const leadMessage = useGetAiLeadEmailMessageQuery(
    isNotNil(leadMessageId) && leadMessageId.length > 0 ? {id: leadMessageId} : skipToken
  );
  const minipurchaseId = kind === 'INTEREST' ? interest.currentData?.minipurchasesIds?.[0] : null;
  const minipurchase = useGetMinipurchaseQuery(
    isNotNil(minipurchaseId) && minipurchaseId.length > 0 ? {minipurchaseId} : skipToken
  );
  const businessCase = useGetBusinessCaseQuery(
    kind === 'BUSINESS_CASE' && isNotNil(id) ? {businessCaseId: id} : skipToken
  );
  const workshopInterest = useGetWorkshopInterestQuery(
    kind === 'WORKSHOP_INTEREST' && isNotNil(id) ? {id} : skipToken
  );
  const serviceCase = useGetMetadaServiceCaseQuery(
    kind === 'SERVICE_CASE' && isNotNil(id) ? {serviceCaseId: id} : skipToken
  );
  const orderParent = useGetServiceCaseByServiceOrderIdQuery(
    kind === 'SERVICE_ORDER' && isNotNil(id) ? {serviceOrderId: id} : skipToken
  );
  const serviceCaseId = orderParent.currentData?.id;
  const serviceOrder = useGetServiceOrderQuery(
    kind === 'SERVICE_ORDER' && isNotNil(id) && isNotNil(serviceCaseId) && serviceCaseId.length > 0
      ? {serviceCaseId, serviceOrderId: id}
      : skipToken
  );
  const rentalOrder = useGetRentalOrderQuery(
    kind === 'RENTAL_ORDER' && isNotNil(id) ? {rentalOrderId: id} : skipToken
  );

  // A business case links a sale vehicle, not the base vehicle consumed by getVehicle. The
  // sale-vehicle endpoint is keyed by the vehicle, so the offer's vehicle id is the reliable key;
  // `saleVehicleId` is the sale vehicle's own id and only works where the two coincide.
  const saleVehicleId =
    kind === 'BUSINESS_CASE'
      ? (businessCase.currentData?.offers?.[0]?.saleVehicles?.[0]?.vehicleId ??
        businessCase.currentData?.saleVehicleId)
      : null;
  const saleVehicle = useGetSaleVehicleQuery(
    isNotNil(saleVehicleId) && saleVehicleId.length > 0 ? {vehicleId: saleVehicleId} : skipToken
  );
  const vehicleId = match(kind)
    .with('BUSINESS_CASE', () => (saleVehicle.isError ? null : saleVehicle.currentData?.vehicleId))
    .with('SERVICE_CASE', () => serviceCase.currentData?.vehicleId)
    .with('SERVICE_ORDER', () => orderParent.currentData?.vehicleId)
    .with('WORKSHOP_INTEREST', () => workshopInterest.currentData?.vehicleId)
    .otherwise(() => null);
  const relatedVehicle = useGetVehicleQuery(
    isNotNil(vehicleId) && vehicleId.length > 0 ? {vehicleId} : skipToken
  );

  const selected = match(kind)
    .returnType<{source: AiComposerContextSource | null; queries: QueryState[]}>()
    .with('CUSTOMER', (context) => ({
      queries: [customer],
      source: isNotNil(customer.currentData)
        ? {context, data: {name: customerName(customer.currentData)}}
        : null,
    }))
    .with('VEHICLE', (context) => ({
      queries: [directVehicle],
      source: isNotNil(directVehicle.currentData)
        ? {context, data: directVehicle.currentData}
        : null,
    }))
    .with('INTEREST', (context) => ({
      queries: [interest],
      source: isNotNil(interest.currentData) ? {context, data: interest.currentData} : null,
    }))
    .with('BUSINESS_CASE', (context) => ({
      queries: [businessCase],
      source: isNotNil(businessCase.currentData) ? {context, data: businessCase.currentData} : null,
    }))
    .with('WORKSHOP_INTEREST', (context) => ({
      queries: [workshopInterest],
      source: isNotNil(workshopInterest.currentData)
        ? {context, data: workshopInterest.currentData}
        : null,
    }))
    .with('SERVICE_CASE', (context) => ({
      queries: [serviceCase],
      source: isNotNil(serviceCase.currentData) ? {context, data: serviceCase.currentData} : null,
    }))
    .with('SERVICE_ORDER', (context) => ({
      queries: isNotNilOrEmpty(orderParent.currentData?.id)
        ? [orderParent, serviceOrder]
        : [orderParent],
      source:
        isNotNil(serviceOrder.currentData) && isNotNilOrEmpty(orderParent.currentData?.id)
          ? {context, data: serviceOrder.currentData}
          : null,
    }))
    .with('RENTAL_ORDER', (context) => ({
      queries: [rentalOrder],
      source: isNotNil(rentalOrder.currentData) ? {context, data: rentalOrder.currentData} : null,
    }))
    .otherwise(() => ({queries: [], source: null}));

  const requiredQueries = [tenant, recipient, ...selected.queries];
  const optionalQueries = [
    ...(isNotNilOrEmpty(leadMessageId) ? [leadMessage] : []),
    ...(isNotNilOrEmpty(minipurchaseId) ? [minipurchase] : []),
    ...(isNotNilOrEmpty(saleVehicleId) ? [saleVehicle] : []),
    ...(isNotNilOrEmpty(vehicleId) ? [relatedVehicle] : []),
  ];
  const isError = requiredQueries.some((query) => query.isError);
  const isLoading = [...requiredQueries, ...optionalQueries].some(
    (query) => query.isFetching || query.isUninitialized
  );

  return (
    match({enabled, isError, isLoading})
      .returnType<RecordContext>()
      // Without a recipient there is nothing to load yet; only a record that cannot be resolved fails.
      .with({enabled: false}, () => ({
        context: null,
        isLoading: false,
        isError: params.isEnabled && isNilOrEmpty(record?.customerId) === false,
      }))
      .with({isError: true}, () => ({context: null, isLoading: false, isError: true}))
      .with({isLoading: true}, () => ({context: null, isLoading: true, isError: false}))
      .otherwise(() => {
        if (isNil(selected.source) || isNil(recipient.currentData) || isNil(tenant.currentData)) {
          return {context: null, isLoading: false, isError: true};
        }
        const context = projectAiComposerRecordContext({
          source: selected.source,
          replyTo: record?.replyTo,
          recipient: {
            name: customerName(recipient.currentData),
            preferredLocale: recipient.currentData.preferredLocale,
          },
          tenant: tenant.currentData,
          vehicle:
            isNotNilOrEmpty(vehicleId) && !relatedVehicle.isError
              ? relatedVehicle.currentData
              : null,
          saleVehicle:
            isNotNilOrEmpty(saleVehicleId) && !saleVehicle.isError ? saleVehicle.currentData : null,
          interestRequest: {
            leadMessage:
              isNotNilOrEmpty(leadMessageId) && !leadMessage.isError
                ? leadMessage.currentData
                : null,
            minipurchase:
              isNotNilOrEmpty(minipurchaseId) && !minipurchase.isError
                ? minipurchase.currentData
                : null,
          },
          translate: (key) => i18n.t(key),
        });
        return {context, isLoading: false, isError: isNil(context)};
      })
  );
}
